/* <antarti-seq>: the scroll-played hero film (the Spotlight film's player). The long-form guide to the contract,
 * the tiers, the decode budget and the size clips is the comment at the top of sections/spotlight-film.liquid. */
(() => {
  'use strict';

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const reduceChange = window.matchMedia('(prefers-reduced-motion: reduce)');
  const MB = 1024 * 1024;
  const pad = (i) => String(i).padStart(3, '0');
  const W = 16;
  const INFLIGHT = 8;
  const SLOW_MS = 22;   // ms a 1920x1080 frame, scaled by the tier's pixels: a slower decoder steps down a tier (header)
  const LAG = 4, LAG_MS = 300;   // the lag watch: frames behind, for how long (header)
  const GAP = 3;   // the widest gap render() blends across (a slow decoder's sparse window)
  const DPR = () => Math.min(window.devicePixelRatio || 1, 3);
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
  const REST_FADE = 250;   // ms
  const HI_FADE = 150;   // ms, the sharp still over the playback tier's frame
  const MIN_SLOTS = 16;   // fewer decoded frames than this starve on a wheel: play a smaller tier, sharpen on stop
  const SZ_MB = { desk: 120, phone: 48 };   // the size clips' decode window
  const SZ_XF = 2.5;   // frames, the crossfade at edge-on
  const SZ_RM = 300;   // ms, reduced motion: the rest stills' crossfade
  const ease = (t) => t * t * (3 - 2 * t);

  class AntartiSeq extends HTMLElement {
    get rm() { return this.static || reduce.matches; }
    at(path) { return new URL(path, this.base).href; }

    async connectedCallback() {
      const ac = this.ac = new AbortController();
      this.canvas = this.querySelector('canvas');
      this.ctx = this.canvas.getContext('2d', { alpha: false });
      this.static = this.hasAttribute('data-static') || !!navigator.connection?.saveData;   // Save-Data: the still
      this.gen = (this.gen || 0) + 1;
      try {
        this.manifestUrl = new URL(this.dataset.manifest, location.href).href;
        this.base = new URL(this.dataset.frames || this.manifestUrl, location.href).href;
        this.meta = await fetch(this.manifestUrl, { signal: ac.signal }).then((r) => (r.ok ? r.json() : null));
      } catch { this.meta = null; }
      if (ac.signal.aborted) return;
      if (!this.meta?.count || !this.meta.cameras) return this.fail();
      this.stoneUrl = this.at(this.meta.stone || 'hero-stone.webp');
      const vh = +this.dataset.vh || this.meta.heroVh;
      if (vh > 0) this.style.setProperty('--hero-vh', String(vh));
      this.count = this.meta.count;
      this.last = this.count - 1;
      this.phases = this.meta.phases;
      this.current = -1; this.drawn = -1; this.pos = -1; this.settled = false;
      this.target = 0; this.shown = 0; this.vel = 0; this.dir = 1;
      this.bitmaps = new Map();   // frame -> ImageBitmap
      this.pending = new Set();   // frames being decoded
      this.inflight = 0;
      this.rest = null;           // { bmp } the sharp rest image
      this.restA = 0;             // its cross-fade, 0..1

      this.pSpiral = this.pOf(this.phases.heroSet ?? this.phases.spiralEnd);
      this.pShut = this.pOf(this.phases.claspShut);
      this.pRest = this.pOf(this.phases.rest ?? this.last);
      this.reserved = 0;

      this.onResize = async () => {
        await this.pickCamera();
        this.fit();
        if (this.rm) { this.classList.add('is-static'); this.showRest(); }
      };
      this.onScroll = () => { this.moved = true; this.update(); };
      this.onMotion = (e) => this.static || (e.matches ? this.still() : this.play());
      window.addEventListener('resize', this.onResize, { passive: true });
      window.addEventListener('orientationchange', this.onResize, { passive: true });
      window.addEventListener('scroll', this.onScroll, { passive: true });
      reduceChange.addEventListener?.('change', this.onMotion);

      await this.pickCamera(true);
      if (ac.signal.aborted || this.failed) return;
      this.fit();
      let t0 = 0;
      this.loop = (now) => {
        const dt = Math.min(100, t0 ? now - t0 : 16.67);
        t0 = now;
        this.follow(dt / 1000);
        this.pump(Math.round(this.shown));
        this.watch(dt, now);
        if (this.C.rest && this.drawn >= (this.phases.lightsDown ?? this.last)) this.loadRest();
        this.render(dt);
        if (this.pub) { this.pub = false; this.publish(); }
        this.raf = requestAnimationFrame(this.loop);
      };
      if (this.rm) this.still(); else this.play(true);
      // off screen (the rooms below): no loop, no decoded frames; the encoded frames stay, so coming back is instant
      this.io = new IntersectionObserver(([e]) => {
        if (this.rm || this.failed || e.isIntersecting === !this.away) return;
        this.away = !e.isIntersecting;
        if (this.away) { cancelAnimationFrame(this.raf); this.drop(true); this.szDrop(); } else this.play();
      });
      this.io.observe(this);
    }

    disconnectedCallback() { this.stop(); }

    stop() {
      this.ac?.abort();
      this.gen = (this.gen || 0) + 1;
      cancelAnimationFrame(this.raf);
      this.io?.disconnect();
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('orientationchange', this.onResize);
      window.removeEventListener('scroll', this.onScroll);
      reduceChange.removeEventListener?.('change', this.onMotion);
      this.blobs = null;
      if (this.bitmaps) this.drop();
      this.szDrop();
    }

    fail() {
      this.failed = true;
      this.stop();
      this.classList.add('is-failed');
      this.dispatchEvent(new CustomEvent('antarti-seq:failed', { bubbles: true }));
    }

    still() {
      cancelAnimationFrame(this.raf);
      this.drop(true);
      this.szDrop();
      this.classList.add('is-static');
      this.publish(1);
      return this.showRest();
    }
    play(first) {
      this.classList.remove('is-static');
      this.update();
      this.shown = this.target; this.vel = 0; this.dirty = true;
      if (this.rest) this.rest.shown = false;
      if (!first) this.preload(this.gen);   // at load pickCamera() has started it
      cancelAnimationFrame(this.raf);
      this.raf = requestAnimationFrame(this.loop);
    }

    // ---- camera, tier, format ----
    async pickCamera(first) {
      const cam = window.innerWidth < window.innerHeight ? 'port' : 'land';
      const C = this.meta.cameras[cam];
      const need = (this.canvas.clientWidth ? this.frameToCss(0, 0, C, true).w : window.innerWidth) * DPR();
      const tiers = [...C.tiers].sort((a, b) => a.w - b.w);
      const sharp = tiers.find((t) => t.w >= need) || tiers[tiers.length - 1];
      if (!first && cam === this.cam && sharp.w <= this.sharp.w) return;   // same camera: never step down
      this.cam = cam; this.C = C; this.sharp = sharp; this.tiers = tiers;
      const phone = this.phone = Math.min(window.innerWidth, window.innerHeight) < 600;
      const rest = this.restBytes(C), hb = (t) => (t === sharp ? 0 : sharp.w * sharp.h * 4);
      const budget = (t) => Math.min((phone ? 160 : 420) * MB + hb(t), Math.max((phone ? 120 : 250) * MB, 26 * t.w * t.h * 4 + rest + hb(t)));
      const cap = this.cap?.[cam] ?? Infinity;
      let tier = [...tiers].reverse().find((t) => t.w <= Math.min(cap, sharp.w)) || tiers[0];
      while (tier !== tiers[0] && (budget(tier) - rest - hb(tier)) / (tier.w * tier.h * 4) < MIN_SLOTS) tier = tiers[tiers.indexOf(tier) - 1];
      this.tier = tier;
      this.budget = budget(tier);
      this.pace();
      this.since = performance.now();   // the lag watch waits for the new tier's first frames
      this.drop();
      this.restUrl = '';   // until this camera's format is known (no early rest decode of the old camera's image)
      const gen = ++this.gen;
      this.blobs = new Array(this.count);
      const fmt = await this.pickFormat(tier, gen, Math.round(this.frameOf(this.progress())));
      if (gen !== this.gen) return;
      if (!fmt) return this.fail();
      this.fmt = fmt;
      this.restUrl = new URL(this.restSrc(), location.href).href;
      for (const el of [document.documentElement, this]) el.dataset.seqCam = cam;
      this.dispatchEvent(new CustomEvent('antarti-seq:camera', { bubbles: true, detail: { cam, tier, fmt: this.fmt, restUrl: this.restUrl } }));
      this.preload(gen);
      if (!this.rm && !(this.probed ||= new Set()).has(cam)) { this.probed.add(cam); this.probe(gen); }
    }

    // ---- the decoder's pace (see the header) ----
    limit(t) { return SLOW_MS * t.w * t.h / (1920 * 1080); }
    lower() { return this.tiers.filter((t) => t.w < this.tier.w).pop(); }
    async timeDecode(blobs) {
      const t0 = performance.now();
      const bm = await Promise.all(blobs.map((b) => createImageBitmap(b).catch(() => null)));
      const ms = (performance.now() - t0) / blobs.length;
      bm.forEach((b) => b?.close());
      return bm.every(Boolean) ? ms : Infinity;
    }
    async idle(gen) {
      await new Promise((r) => requestAnimationFrame(() => setTimeout(r, 0)));
      for (let k = 0; k < 60 && gen === this.gen && (this.inflight || this.vel || this.shown !== this.target); k++) await sleep(50);
    }
    async probe(gen) {
      for (let k = 0; k < 60 && gen === this.gen && this.blobs.filter(Boolean).length < 6; k++) await sleep(50);
      await this.idle(gen);
      if (gen !== this.gen) return;
      const tier = this.tier, ms = await this.timeDecode(this.blobs.filter(Boolean).slice(0, 6));
      if (gen !== this.gen) return;
      this.decodeMs = ms; this.probePx = tier.w * tier.h; this.pace();
      const lo = this.lower();
      if (ms > this.limit(tier) && lo) { this.cap = { ...this.cap, [this.cam]: lo.w }; this.undo = { cam: this.cam, tier }; this.pickCamera(true); }
    }
    async retry(gen) {
      const { cam, tier } = this.undo;
      this.undo = null;
      await this.idle(gen);
      const f = Math.round(this.target), ids = [0, 1, 2, 3, 4, 5].map((k) => Math.min(this.last, f + k));
      const bl = (await Promise.all(ids.map((i) => this.fetchBlob(this.at(tier.dir + pad(i) + '.' + this.fmt))))).filter(Boolean);
      if (gen !== this.gen || cam !== this.cam || bl.length < 6) return;
      const ms = await this.timeDecode(bl);
      if (gen !== this.gen || ms > this.limit(tier)) return;
      this.decodeMs = ms; this.probePx = tier.w * tier.h;
      delete this.cap[cam];
      this.pickCamera(true);
    }
    watch(dt, now) {
      const c = Math.round(this.shown), lag = Math.abs(this.pos - this.shown);
      if (now - this.since > 1500 && this.pos >= 0 && this.blobs?.[c] && lag > LAG && lag > 0.15 * Math.abs(this.vel)) this.lagT = (this.lagT || 0) + dt;
      else this.lagT = 0;
      if (this.lagT <= LAG_MS || !this.lower()) return;
      this.lagT = 0;
      this.undo = null;   // a step that playback itself forced is not undone
      this.cap = { ...this.cap, [this.cam]: this.lower().w };
      this.pickCamera(true);
    }
    pace() {
      const ms = this.decodeMs ? this.decodeMs * this.tier.w * this.tier.h / this.probePx : 0, L = this.limit(this.tier);
      this.stride = ms > 0.75 * L ? Math.min(GAP, 1 + Math.ceil(ms / L)) : 1;
      this.maxFlight = this.stride > 1 ? 2 : INFLIGHT;
    }

    async pickFormat(tier, gen, at = 0) {
      const f = this.meta.formats?.length ? this.meta.formats : ['webp'];
      for (const fmt of f) {
        const b = await this.fetchBlob(this.at(tier.dir + pad(at) + '.' + fmt));
        if (gen !== this.gen) return;   // a newer camera or tier took over: leave its blobs and bitmaps alone
        const bmp = b && await createImageBitmap(b).catch(() => null);
        if (gen !== this.gen) { if (bmp) bmp.close(); return; }
        if (bmp) { this.blobs[at] = b; if (this.rm) bmp.close(); else this.keep(at, bmp); return fmt; }
      }
      return null;   // frame 0 decodes in no listed format
    }

    restSrc() {
      const r = this.C.rest;
      if (r) return this.at(r.src + '.' + this.fmt);
      return this.at(this.tiers[this.tiers.length - 1].dir + pad(this.last) + '.' + this.fmt);
    }

    drop(keepRest) {
      for (const b of this.bitmaps.values()) b.close();
      this.bitmaps.clear();
      this.pending.clear();   // decodes in flight are closed when they land
      this.hi?.bmp?.close(); this.hi = null; this.hiA = 0;
      if (keepRest) return;
      if (this.rest?.bmp) this.rest.bmp.close();
      this.rest = null; this.restA = 0;
    }

    // ---- loading: every frame as an encoded Blob ----
    // Shopify's CDN negotiates on Accept: fetch() sends */* and gets a re-encoded JPEG; asking for AVIF/WebP gets
    // the original file byte-for-byte (checked 18 Oct 2026).
    fetchBlob(url) { return fetch(url, { signal: this.ac.signal, headers: { Accept: 'image/avif,image/webp,*/*' } }).then((r) => (r.ok ? r.blob() : null)).catch(() => null); }

    order(first) {
      const { handoffStart = 0, handoffEnd = 0 } = this.phases, seen = new Set(), out = [];
      const add = (i) => { if (i >= 0 && i < this.count && !seen.has(i)) { seen.add(i); out.push(i); } };
      add(first); add(0);
      for (let i = handoffStart; i <= handoffEnd; i++) add(i);
      add(this.last);
      for (let i = 0; i < this.count; i += 8) add(i);
      for (let i = 0; i < this.count; i++) add(i);
      return out;
    }

    async preload(gen) {
      if (this.rm) return;
      // the page's own requests first; then only ~120 frames until the visitor scrolls
      if (document.readyState !== 'complete') await new Promise((r) => addEventListener('load', r, { once: true }));
      const q = this.order(Math.round(this.target));
      let k = 0;
      const worker = async () => {
        while (k < q.length && gen === this.gen && !this.rm) {
          if (k >= 120 && !this.moved) { await sleep(200); continue; }
          const i = q[k++];
          if (this.blobs[i]) continue;
          const b = await this.fetchBlob(this.at(this.tier.dir + pad(i) + '.' + this.fmt));
          if (gen !== this.gen) return;
          this.blobs[i] = b;
          if (b && Math.abs(i - Math.round(this.shown)) < 12) this.pump(Math.round(this.shown));
        }
      };
      await Promise.all([worker(), worker(), worker(), worker(), worker(), worker()]);
      if (gen === this.gen && this.undo?.cam === this.cam) this.retry(gen);
    }

    // ---- decoding: a window of bitmaps around the drawn frame, inside the budget ----
    frameBytes() { return this.tier.w * this.tier.h * 4; }
    restSize(C = this.C) {
      const r = C.rest;
      if (!r) return null;
      const d = this.frameToCss(0, 0, C), dpr = DPR();
      const w = Math.min(r.w, Math.round(d.w * dpr));
      return { w, h: Math.round(w * r.h / r.w) };
    }
    restBytes(C = this.C) { const z = this.restSize(C); return z ? z.w * z.h * 4 : 0; }
    hiBytes() { return this.sharp === this.tier ? 0 : this.sharp.w * this.sharp.h * 4; }
    slots() {
      const n = Math.floor((this.budget - this.restBytes() - this.hiBytes() - this.reserved) / this.frameBytes());
      return this.reserved ? Math.max(1, n) : Math.max(3, n);
    }
    windowOf(c) {
      const k = this.stride || 1, n = Math.min(k > 1 ? Math.max(1, this.slots() - 3) * k : this.slots(), this.count);
      const ahead = Math.ceil((n - 1) * 0.7), behind = n - 1 - ahead;
      let lo = this.dir >= 0 ? c - behind : c - ahead, hi = this.dir >= 0 ? c + ahead : c + behind;
      if (lo < 0) { hi -= lo; lo = 0; }
      if (hi > this.last) { lo -= hi - this.last; hi = this.last; }
      lo = Math.max(0, lo);
      const ph = this.phases;
      for (const [a, b] of [[0, ph.handoffEnd ?? 0], [ph.claspShut ?? this.last, this.last]]) {
        if (c < a || c > b || b - a + 1 > n) continue;
        if (lo > a) { hi -= lo - a; lo = a; }
        if (hi < b) { lo += b - hi; hi = b; }
      }
      return [lo, hi];
    }
    wanted(c = Math.round(this.shown)) {
      const [lo, hi] = this.windowOf(c), k = this.stride || 1, T = Math.round(this.target);
      return Object.assign((i) => (i >= lo && i <= hi && (k === 1 || i % k === 0)) || (k > 1 && (i === c || i === T)), { lo, hi, T, k });
    }
    pump(c) {
      if (!this.blobs || this.rm) return;
      const want = this.wanted(c), { lo, hi, T, k } = want, n = this.slots();
      for (const [i, b] of this.bitmaps) if (!want(i)) { b.close(); this.bitmaps.delete(i); }
      const far = k > 1 && (T < lo || T > hi) ? [T] : [];
      for (let d = -1; d <= hi - lo; d++) {
        for (const i of d < 0 ? far : d === 0 ? [c] : this.dir >= 0 ? [c + d, c - d] : [c - d, c + d]) {
          if (this.inflight >= (this.maxFlight || INFLIGHT) || this.bitmaps.size + this.pending.size >= n) return;
          if (!want(i) || this.bitmaps.has(i) || this.pending.has(i) || !this.blobs[i]) continue;
          const gen = this.gen;
          this.pending.add(i); this.inflight++;
          createImageBitmap(this.blobs[i]).then((bmp) => {
            this.inflight--;
            if (gen !== this.gen || !this.pending.has(i)) { bmp.close(); return; }
            this.pending.delete(i);
            if (this.wanted()(i)) this.keep(i, bmp); else bmp.close();
            this.pump(Math.round(this.shown));   // the next decode starts now, not on the next refresh
          }, () => { this.inflight--; this.pending.delete(i); });
        }
      }
    }
    keep(i, bmp) {
      this.bitmaps.get(i)?.close();
      this.bitmaps.set(i, bmp);
      this.dirty = true;   // the loop repaints with it
    }
    reserve(bytes, key = 'page') {
      this.held = { ...this.held, [key]: Math.max(0, bytes || 0) };
      this.reserved = Object.values(this.held).reduce((a, b) => a + b, 0);
      this.pump(Math.round(this.shown));
    }
    memory() {
      let bytes = 0;
      for (const b of this.bitmaps.values()) bytes += b.width * b.height * 4;
      if (this.rest?.bmp) bytes += this.rest.bmp.width * this.rest.bmp.height * 4;
      if (this.hi?.bmp) bytes += this.hi.bmp.width * this.hi.bmp.height * 4;
      return { live: this.bitmaps.size + (this.rest?.bmp ? 1 : 0), bytes, budget: this.budget, reserved: this.reserved };
    }

    // ---- the sharp rest image ----
    loadRest() {
      if (!this.rest && this.restUrl) {
        const r = this.rest = { bmp: null, shown: false }, gen = this.gen;
        const z = this.restSize();
        const opt = z && z.w < this.C.rest.w ? { resizeWidth: z.w, resizeHeight: z.h, resizeQuality: 'high' } : undefined;
        r.ready = this.fetchBlob(this.restUrl).then((b) => b && createImageBitmap(b, opt).catch(() => createImageBitmap(b)).catch(() => null)).then((bmp) => {
          if (gen === this.gen && this.rest === r) r.bmp = bmp; else if (bmp) bmp.close();
        });
      }
      return this.rest?.ready;
    }
    loadHi(i) {
      if (this.hi?.i === i) return;
      this.hi?.bmp?.close();
      const h = this.hi = { i, bmp: null }, gen = this.gen;
      this.fetchBlob(this.at(this.sharp.dir + pad(i) + '.' + this.fmt)).then((b) => b && createImageBitmap(b).catch(() => null)).then((bmp) => {
        if (gen === this.gen && this.hi === h && bmp) { h.bmp = bmp; this.dirty = true; } else bmp?.close();
      });
    }
    async showRest() {
      await this.loadRest();
      const r = this.rest;
      if (!r || r.shown || !this.rm) return;
      if (!r.bmp && !r.fallback) {
        r.fallback = true;
        const b = this.blobs[this.last] || await this.fetchBlob(this.at(this.tier.dir + pad(this.last) + '.' + this.fmt));
        const bmp = b && await createImageBitmap(b).catch(() => null);
        if (this.rest === r && !r.bmp) r.bmp = bmp; else if (bmp) bmp.close();
      }
      if (!r.bmp || this.rest !== r || r.shown) return;
      this.paint(this.szRest(this.sz?.c)?.bmp || r.bmp);
      r.shown = true;
      this.pos = this.last;
      this.setDrawn(this.last);
      this.publish();
    }

    // ---- drawing ----
    fit() {
      const r = this.canvas.getBoundingClientRect();
      const dpr = DPR();
      this.canvas.width = Math.max(1, Math.round(r.width * dpr));
      this.canvas.height = Math.max(1, Math.round(r.height * dpr));
      if (this.rest) this.rest.shown = false;
      if (this.rest?.bmp && this.C?.rest && this.rest.bmp.width < this.restSize().w) { this.rest.bmp.close(); this.rest = null; this.restA = 0; }
      this.dirty = true;   // resizing the backing store cleared it
    }

    /* The cover fit, in CSS px of the canvas's box: the same maths paint() uses (in device px). raw: without snap(). */
    frameToCss(fx, fy, C = this.C, raw) {
      const cw = this.canvas.clientWidth, ch = this.canvas.clientHeight;
      const { w: aw, h: ah } = C || { w: 16, h: 9 };
      const s = Math.max(cw / aw, ch / ah) * (raw ? 1 : this.snap(C)), w = aw * s, h = ah * s;
      return { x: (cw - w) / 2 + fx * w, y: (ch - h) / 2 + fy * h, w, h };
    }

    snap(C) {
      const t = C && C === this.C && this.sharp, cw = this.canvas.width, ch = this.canvas.height;
      if (!t || !cw) return 1;
      const m = t.w / (C.w * Math.max(cw / C.w, ch / C.h));
      return Math.abs(m - 1) < 0.03 ? m : 1;
    }
    paint(img, alpha = 1) {
      const cw = this.canvas.width, ch = this.canvas.height;
      const { w: aw, h: ah } = this.C, m = this.snap(this.C);
      const s = Math.max(cw / aw, ch / ah) * m;
      let w = aw * s, h = ah * s, x = (cw - w) / 2, y = (ch - h) / 2;
      if (m !== 1) { w = this.sharp.w; h = this.sharp.h; x = Math.round(x); y = Math.round(y); }
      this.ctx.globalAlpha = alpha;
      this.ctx.imageSmoothingQuality = 'high';
      this.ctx.drawImage(img, x, y, w, h);
      this.ctx.globalAlpha = 1;
    }

    follow(dt) {
      const x = this.shown - this.target;
      if (Math.abs(x) > 0.05) this.dir = x < 0 ? 1 : -1;
      const c = this.vel + W * x, e = Math.exp(-W * dt);
      let nx = (x + c * dt) * e, nv = (this.vel - W * c * dt) * e;
      if (nx * x < 0 || (Math.abs(nx) < 0.004 && Math.abs(nv) < 0.05)) { nx = 0; nv = 0; }
      this.shown = Math.min(this.last, Math.max(0, this.target + nx)); this.vel = nv;
    }

    render(dt) {
      if (!this.C) return;
      const raw = Math.max(0, Math.min(this.last, this.shown));
      if (this.vel !== 0 || this.shown !== this.target || this.disp == null) this.disp = raw;
      else {
        const goal = Math.round(raw), step = dt / 150;
        this.disp = Math.abs(goal - this.disp) <= step ? goal : this.disp + Math.sign(goal - this.disp) * step;
      }
      const s = this.disp;
      let a = -1, b = -1, ta = 0;
      for (let k = 0; k <= GAP; k++) {
        if (a < 0 && this.bitmaps.has(Math.floor(s) - k)) a = Math.floor(s) - k;
        if (b < 0 && this.bitmaps.has(Math.ceil(s) + k)) b = Math.ceil(s) + k;
      }
      let pos, base, top = null;
      if (a >= 0 && b >= 0 && b - a <= GAP) {
        ta = b > a ? (s - a) / (b - a) : 0;
        if (ta >= 0.004) { pos = s; base = a; top = this.bitmaps.get(b); } else { pos = a; base = a; }
      } else {   // not decoded yet: the nearest decoded frame on either side
        let best = Infinity, j = -1;
        const i = Math.round(s);
        for (const k of this.bitmaps.keys()) { const d = Math.abs(k - i); if (d < best || (d === best && k < j)) { best = d; j = k; } }
        if (j < 0) { this.szTick(dt, false); return; }   // nothing of the film decoded here: off the rest frame (the sizes hand back)
        pos = j; base = j;
      }
      const still = this.vel === 0 && this.shown === this.target && Math.abs(pos - s) < 0.01;
      const atLast = still && pos > this.last - 0.5 && this.rest?.bmp && this.C.rest;
      const ra = atLast ? Math.min(1, this.restA + dt / REST_FADE) : this.rest?.bmp ? Math.max(0, this.restA - (2 * dt) / REST_FADE) : 0;
      if (still && !top && this.sharp !== this.tier) this.loadHi(pos);
      const ha = still && !top && this.hi?.bmp && this.hi.i === pos ? Math.min(1, (this.hiA || 0) + dt / HI_FADE) : 0;
      const sz = this.szTick(dt, still && pos > this.last - 0.5);
      if (!this.dirty && !sz?.changed && pos === this.pos && ra === this.restA && ha === this.hiA) return this.settle(still);
      this.dirty = false; this.restA = ra; this.hiA = ha;
      if (!(sz?.alpha >= 1 && this.szDraw(1))) {
        this.paint(this.bitmaps.get(base));
        if (top) this.paint(top, ta);
        if (ha > 0) this.paint(this.hi.bmp, ha);
        if (ra > 0) this.paint(this.rest.bmp, ra * ra * (3 - 2 * ra));
        if (sz) this.szDraw(sz.alpha);
      }
      const moved = pos !== this.pos;
      this.pos = pos;
      this.setDrawn(Math.round(pos), moved);
      this.settle(still);
    }

    settle(still) {
      const settled = still && Math.round(this.pos) >= (this.phases.rest ?? this.last);
      if (settled === this.settled) return;
      this.settled = settled;
      for (const el of [document.documentElement, this]) el.toggleAttribute('data-seq-settled', settled);
      this.pub = true;
    }

    setDrawn(j, moved) {
      this.current = this.drawn = j;
      if (moved) this.pub = true;
    }

    cueTable() {
      const cam = this.cam, T = (this.cueT ||= {});
      if (T[cam]) return T[cam];
      const ld = this.phases.lightsDown, col = this.meta.colours?.[cam], tab = new Float32Array(this.count);
      if (ld == null) return (T[cam] = tab);
      const hex = (h) => { const n = parseInt(String(h).slice(1), 16); return 0.2126 * (n >> 16) + 0.7152 * ((n >> 8) & 255) + 0.0722 * (n & 255); };
      const L = (f) => (col?.[f] ? (hex(col[f].top) + hex(col[f].bottom) + hex(col[f].left) + hex(col[f].right)) / 4 : 0);
      const L0 = L(ld), L1 = L(this.last), end = Math.max(ld + 1, this.phases.rest ?? this.last);
      for (let f = Math.max(0, ld), m = 0; f < this.count; f++) {
        const v = L0 - L1 > 8 ? (L0 - L(f)) / (L0 - L1) : (f - ld) / (end - ld);
        tab[f] = m = Math.max(m, Math.min(1, Math.max(0, v)));
      }
      return (T[cam] = tab);
    }
    cueAt(pos) {
      if (pos < 0) return 0;
      const tab = this.cueTable(), a = Math.floor(pos), b = Math.min(this.last, a + 1);
      return tab[a] + (tab[b] - tab[a]) * (pos - a);
    }

    // ---- the sizes (see the header) ----
    get home() { return String(this.meta.anchors?.[this.cam]?.measure?.mm ?? 5); }
    sizesOpen(on) {
      this.szOn = !!on && !!this.meta.sizes;
      if (this.szOn) this.szPrep();
      else if (this.sz) { this.playSize(null, this.home); this.szPump(); }
    }
    playSize(from, to) {
      const S = this.meta.sizes;
      to = String(to);
      if (!S?.[to]) return;
      let z = this.sz;
      if (!z) {
        if (to === String(from ?? this.home)) return;
        z = this.szPrep(String(from ?? this.home));
        if (!z) return;
      }
      if (!z.on) { z.alpha = this.atLast ? 1 : 0; z.rA = 1; z.at = 0; }
      z.T = to;
      z.on = true;
      if (this.rm || this.classList.contains('is-static')) return this.szStatic();
      if (z.alpha === 0) this.szLand();
      this.szPump();
      this.szSig();
      this.dirty = true;
    }
    szPrep(c) {
      const S = this.meta.sizes;
      if (!S || !this.restUrl || !this.tier) return null;
      const old = this.sz;
      if (old?.gen === this.gen) return old;
      if (old) this.szDrop(true);
      const tiers = [...S.cameras[this.cam].tiers].sort((a, b) => a.w - b.w);
      const tier = [...tiers].reverse().find((t) => t.w <= this.tier.w) || tiers[0];
      const fmts = S.formats || this.meta.formats || ['webp'];
      const T = old?.T ?? c ?? this.home;
      const z = this.sz = {
        gen: this.gen, tier, fmt: fmts.includes(this.fmt) ? this.fmt : fmts[0], E: S.edge ?? S.frames - 1, fps: S.fps || 24,
        c: T, T, f: 0, v: 0, prev: null, on: old?.on ?? false, alpha: old ? 0 : 1, rA: 1, rm: 0,
        blobs: {}, bmps: new Map(), pending: new Set(), inflight: 0, rests: {},
      };
      const bytes = tier.w * tier.h * 4, rz = this.stillSize(S[this.home]?.rest?.[this.cam]);
      z.slots = Math.max(8, Math.min(48, Math.floor((this.phone ? SZ_MB.phone : SZ_MB.desk) * MB / bytes)));
      this.reserve((this.rm ? 0 : z.slots * bytes) + (rz ? 3 * rz.w * rz.h * 4 : 0), 'sizes');
      if (!this.rm) this.szFetch(z);
      for (const d of Object.keys(S)) if (S[d]?.rest && d !== this.home) this.szRest(d, true);
      this.szSig();
      return z;
    }
    stillSize(r) {
      if (!r) return null;
      const w = Math.min(r.w, Math.round(this.frameToCss(0, 0).w * DPR()));
      return { w, h: Math.round(w * r.h / r.w) };
    }
    szRest(d, load) {
      const z = this.sz;
      if (!z || d == null) return null;
      if (d === this.home) { if (load) this.loadRest(); return this.rest; }
      if (z.rests[d] || !load) return z.rests[d] || null;
      const r = this.meta.sizes[d]?.rest?.[this.cam];
      if (!r) return null;
      const st = z.rests[d] = { bmp: null }, sz = this.stillSize(r);
      const opt = sz.w < r.w ? { resizeWidth: sz.w, resizeHeight: sz.h, resizeQuality: 'high' } : undefined;
      st.ready = this.fetchBlob(this.at(r.src + '.' + z.fmt)).then((b) => b && createImageBitmap(b, opt).catch(() => createImageBitmap(b)).catch(() => null)).then((bmp) => {
        if (this.sz === z) { st.bmp = bmp; this.dirty = true; } else bmp?.close();
      });
      return st;
    }
    async szFetch(z) {
      const S = this.meta.sizes, ds = Object.keys(S).filter((d) => S[d]?.frames), q = [];
      for (let i = 0; i <= z.E; i++) q.push([z.c, i]);
      for (let i = z.E; i >= 0; i--) for (const d of ds) if (d !== z.c) q.push([d, i]);
      for (const d of ds) z.blobs[d] = [];
      let k = 0;
      const worker = async () => {
        while (k < q.length && this.sz === z) {
          const [d, i] = q[k++];
          const b = await this.fetchBlob(this.at(z.tier.dir + d + '_' + pad(i) + '.' + z.fmt));
          if (this.sz !== z) return;
          z.blobs[d][i] = b;
          this.szPump();
        }
      };
      await Promise.all([worker(), worker(), worker(), worker(), worker(), worker()]);
    }
    szPath(z) {
      const S = this.meta.sizes, out = [], { c, T, E } = z, f = Math.max(0, Math.min(E, z.f));
      if (z.prev) out.push([z.prev.c, E]);
      if (c === T) for (let i = Math.ceil(f); i >= 0; i--) out.push([c, i]);
      else {
        const a = [], b = [], K = Math.min(4, z.slots >> 1);
        for (let i = Math.floor(f); i <= E; i++) a.push([c, i]);
        for (let i = E; i >= 0; i--) b.push([T, i]);
        out.push(...a.slice(0, z.slots - K - out.length), ...b.slice(0, K), ...a.slice(z.slots - K - out.length), ...b.slice(K));
      }
      if (this.szOn) {
        for (let i = 1; i <= E; i++) out.push([T, i]);
        for (let i = E; i >= 0; i--) for (const d of Object.keys(S)) if (S[d]?.frames && d !== T) out.push([d, i]);
      }
      const seen = new Set();
      for (const [d, i] of out) { seen.add(d + '_' + i); if (seen.size >= z.slots) break; }
      return seen;
    }
    szPump() {
      const z = this.sz;
      if (!z || z.gen !== this.gen || this.rm) return;
      const want = this.szPath(z);
      for (const [k, b] of z.bmps) if (!want.has(k)) { b.close(); z.bmps.delete(k); }
      for (const k of want) {
        if (z.inflight >= INFLIGHT) return;
        if (z.bmps.has(k) || z.pending.has(k)) continue;
        const [d, i] = k.split('_'), blob = z.blobs[d]?.[+i];
        if (!blob) continue;
        z.pending.add(k); z.inflight++;
        createImageBitmap(blob).then((bmp) => {
          z.inflight--; z.pending.delete(k);
          if (this.sz === z && this.szPath(z).has(k)) { z.bmps.set(k, bmp); this.dirty = true; } else bmp.close();
          this.szPump();
        }, () => { z.inflight--; z.pending.delete(k); });
      }
    }
    szHas(d, i) { return this.sz.bmps.has(d + '_' + i); }
    szLand() {
      const z = this.sz;
      z.c = z.T; z.f = 0; z.v = 0; z.prev = null; z.rA = 1;
    }
    szTick(dt, atLast) {
      this.atLast = atLast;
      if (this.sz && this.sz.gen !== this.gen) this.szPrep();
      const z = this.sz;
      if (!z?.on || z.gen !== this.gen) return null;
      const now = performance.now(), s = z.at ? Math.min(0.25, (now - z.at) / 1000) : dt / 1000;
      z.at = now;
      const A = 4 * z.fps, V = z.fps, E = z.E, was = [z.f, z.c, z.alpha, z.rA, z.prev?.k];
      z.alpha = atLast ? Math.min(1, z.alpha + (2000 * s) / REST_FADE) : Math.max(0, z.alpha - (2000 * s) / REST_FADE);
      if (z.alpha === 0) this.szLand();
      else {
        if (z.prev && (z.prev.k += (s * z.fps) / SZ_XF) >= 1) z.prev = null;
        const dir = z.c === z.T ? -1 : 1, dist = z.c === z.T ? z.f : 2 * E - z.f;
        const vd = dir * Math.min(V, Math.sqrt(2 * A * dist));
        z.v += Math.max(-A * s, Math.min(A * s, vd - z.v));
        let f = z.f + z.v * s;
        if (!this.szHas(z.c, Math.max(0, Math.min(E, z.v > 0 ? Math.ceil(f) : Math.floor(f))))) f = z.f;
        if (f >= E && z.c !== z.T) {
          if (this.szHas(z.T, E) && this.szHas(z.T, Math.max(0, Math.floor(2 * E - f)))) { z.prev = { c: z.c, k: 0 }; z.c = z.T; f = 2 * E - f; z.v = -z.v; } else f = E;
        }
        if (f >= E) { f = E; z.v = Math.min(0, z.v); }
        if (f <= 0) { f = 0; z.v = z.c === z.T ? 0 : Math.max(0, z.v); }
        z.f = f;
      }
      const landed = z.c === z.T && z.f === 0 && z.v === 0 && !z.prev, rest = this.szRest(z.c);
      z.rA = landed && rest?.bmp ? Math.min(1, z.rA + (1000 * s) / REST_FADE) : Math.max(0, z.rA - (2000 * s) / REST_FADE);
      if (z.f !== was[0] || z.c !== was[1]) this.szPump();
      this.szSig();
      if (landed && z.c === this.home && (z.rA >= 1 || !this.C.rest || z.alpha === 0)) {
        z.on = false;
        if (!this.szOn) this.szDrop();
        return { changed: true, alpha: 0 };
      }
      return { changed: was[0] !== z.f || was[1] !== z.c || was[2] !== z.alpha || was[3] !== z.rA || was[4] !== z.prev?.k, alpha: z.alpha };
    }
    szDraw(alpha) {
      const z = this.sz;
      if (!z?.on || alpha <= 0) return false;
      const bm = (d, i) => z.bmps.get(d + '_' + i), a = Math.round(z.f);
      const rest = this.szRest(z.c), ra = rest?.bmp ? z.rA : 0;
      let base = ra >= 1 ? rest.bmp : bm(z.c, a);
      for (let k = 1; k <= z.E && !base; k++) base = bm(z.c, a - k) || bm(z.c, a + k);   // the nearest decoded frame
      if (!base) return false;
      this.paint(base, alpha);
      const pv = z.prev && bm(z.prev.c, z.E);
      if (pv) this.paint(pv, alpha * (1 - ease(Math.min(1, z.prev.k))));
      if (ra > 0 && ra < 1) this.paint(rest.bmp, alpha * ease(ra));
      return true;
    }
    async szStatic() {
      const z = this.sz, to = z.T, from = this.szRest(z.c)?.bmp || this.rest?.bmp, st = this.szRest(to, true);
      await st?.ready;
      if (this.sz !== z || z.T !== to || !st?.bmp) return;
      z.c = to; z.f = 0; z.v = 0; z.prev = null;
      this.szSig();
      cancelAnimationFrame(z.rm);
      const t0 = performance.now();
      const step = (now) => {
        if (this.sz !== z) return;
        const k = Math.min(1, (now - t0) / SZ_RM);
        if (from && from !== st.bmp) { this.paint(from); this.paint(st.bmp, ease(k)); } else this.paint(st.bmp);
        if (k < 1) z.rm = requestAnimationFrame(step);
        else if (!this.szOn && to === this.home) this.szDrop();
      };
      z.rm = requestAnimationFrame(step);
    }
    szSig() {
      const z = this.sz, landed = !z || (z.c === z.T && z.f === 0 && z.v === 0 && !z.prev);
      const size = z ? (landed ? z.c : null) : this.home, target = z?.T ?? size, shown = z?.c ?? this.home;
      const sig = size + '/' + target + '/' + shown;
      if (sig === this.szLast) return;
      this.szLast = sig;
      this.dataset.seqSize = size ?? '';
      this.toggleAttribute('data-seq-swap', !landed);
      this.dispatchEvent(new CustomEvent('antarti-seq:size', { bubbles: true, detail: { size, target, shown } }));
    }
    szDrop(keep) {
      const z = this.sz;
      if (!z) return;
      cancelAnimationFrame(z.rm);
      for (const b of z.bmps.values()) b.close();
      for (const r of Object.values(z.rests)) r.bmp?.close();
      this.sz = null;
      if (!keep) { this.reserve(0, 'sizes'); this.szSig(); }
    }

    // ---- scroll ----
    progress() {
      const r = this.getBoundingClientRect();
      const span = r.height - window.innerHeight;
      return span > 0 ? Math.min(1, Math.max(0, -r.top / span)) : 0;
    }
    frameOf(p) {
      const s = this.meta.scroll;
      for (let k = 1; k < s.length; k++) {
        const [p0, f0] = s[k - 1], [p1, f1] = s[k];
        if (p <= p1) return p1 > p0 ? f0 + (f1 - f0) * Math.max(0, (p - p0) / (p1 - p0)) : f1;
      }
      return s[s.length - 1][1];
    }
    pOf(f) {   // the first scroll point that reaches frame f
      const s = this.meta.scroll;
      for (let k = 1; k < s.length; k++) {
        const [p0, f0] = s[k - 1], [p1, f1] = s[k];
        if (f1 >= f) return f1 > f0 ? p0 + (p1 - p0) * Math.max(0, (f - f0) / (f1 - f0)) : p0;
      }
      return 1;
    }

    update() {
      if (this.rm) return;
      this.p = this.progress();
      this.target = Math.min(this.last, Math.max(0, this.frameOf(this.p)));
      this.pub = true;   // published once per refresh, by the loop
    }

    publish(pIn) {
      const p = pIn ?? this.p ?? 0;
      if (pIn !== undefined) this.p = p;
      const clamp = (x) => Math.min(1, Math.max(0, x));
      const a = this.pSpiral, b = this.pShut;
      const build = clamp(p / a);
      const clasp = clamp((p - a) / Math.max(1e-6, b - a));
      const restP = clamp((p - b) / Math.max(1e-6, 1 - b));
      const phase = p < a ? 'build' : p < b ? 'clasp' : 'rest';
      const frame = this.drawn < 0 ? 0 : this.drawn;
      const pos = this.pos < 0 ? 0 : this.pos;
      const cue = this.rm ? 1 : this.cueAt(pos);
      const f = this.meta.colours[this.cam][frame];
      const vars = { '--seq-p': p.toFixed(4), '--seq-build': build.toFixed(4), '--seq-clasp': clasp.toFixed(4), '--seq-rest': restP.toFixed(4), '--seq-cue': cue.toFixed(4), '--seq-top': f.top, '--seq-bottom': f.bottom, '--seq-left': f.left, '--seq-right': f.right };
      const prev = this.vals || {}, ph = this.rm ? 'rest' : phase;
      for (const el of [document.documentElement, this]) {
        for (const [k, v] of Object.entries(vars)) if (prev[k] !== v) el.style.setProperty(k, v);
        if (el.dataset.seqPhase !== ph) el.dataset.seqPhase = ph;
      }
      this.vals = vars;
      this.dispatchEvent(new CustomEvent('antarti-seq:progress', { bubbles: true, detail: { p, build, clasp, rest: restP, phase, frame, current: frame, pos, cue, settled: this.rm || this.settled, cam: this.cam, colours: f } }));
    }
  }

  if (!customElements.get('antarti-seq')) customElements.define('antarti-seq', AntartiSeq);
})();
